import React, { useMemo } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis, Legend, ResponsiveContainer } from "recharts";
import _ from "lodash";
import { Button } from "antd";
import siemensLogo from "../../images/sie-logo-white-rgb.svg";

const formatTimestamp = (ts) => {
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const getStartOfDay = (timestampMs) => {
    const date = new Date(timestampMs);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

const getDateRange = (history) => {
    // to be replaced once not working with sample data. Returns a minimum of 4 dates to act as the x axis
    if (!history || !history.length) return null;

    const dateRange = [];
    const dayMillis = 24 * 60 * 60 * 1000;
    const startTs = getStartOfDay(history[0].timestamp);
    const endTs = getStartOfDay(history[history.length - 1].timestamp);
    for (let i = startTs; i <= endTs; i += dayMillis) {
        dateRange.push(formatTimestamp(i));
    }

    let ts = history[history.length - 1].timestamp;
    while (dateRange.length < 4) {
        ts += dayMillis;
        dateRange.push(formatTimestamp(ts));
    }

    return dateRange;
};

const SiemensAssetTab = ({ selectedMarker, goToSession }) => {
    const data = useMemo(() => {
        try {
            return JSON.parse(selectedMarker.data);
        } catch {
            return {};
        }
    }, [selectedMarker]);

    const eventHistoryGraph = useMemo(() => {
        if (_.isEmpty(data) || !data?.history?.level_1?.length) return null;

        const dateRange = getDateRange(data?.history?.level_1);
        if (!dateRange) return null;
        const processedData = dateRange.map((date) => {
            const matchingData = _.find(data?.history?.level_1, (i) => formatTimestamp(i.timestamp) === date, null); // is correct to only loop level_1 data here as result doesn't change between levels, just the thresholds
            return {
                date,
                lateral_actual: matchingData?.lateral_actual ?? null,
                vertical_actual: matchingData?.vertical_actual ?? null,
            };
        });

        return (
            <div className="SiemensContentChart">
                <ResponsiveContainer
                    width="100%"
                    height="95%">
                    <LineChart data={processedData}>
                        <XAxis
                            dataKey="date"
                            label={{ value: "Time", position: "insideBottom", offset: -5 }}
                        />
                        <YAxis
                            domain={[0, 2.5]}
                            ticks={[0.0, 0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.25]}
                            label={{ value: "Severity", angle: -90, position: "insideLeft" }}
                        />
                        <Line
                            type="linear"
                            dataKey="lateral_actual"
                            stroke="rgb(33, 150, 243)"
                            name="Lateral Severity"
                            connectNulls={true}
                            strokeWidth={3}
                        />
                        <Line
                            type="linear"
                            dataKey="vertical_actual"
                            stroke="rgb(220, 53, 69)"
                            name="Vertical Severity"
                            connectNulls={true}
                            strokeWidth={3}
                        />
                        <CartesianGrid
                            stroke="#ccc"
                            strokeDasharray="5 5"
                        />
                        <Legend
                            verticalAlign="top"
                            align="center"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }, [data]);

    const currentEntry = useMemo(() => {
        return _.maxBy(data?.history?.level_1, "timestamp") ?? null;
    }, [data]);

    if (!currentEntry) {
        return <div className="SiemensErrorMessage">No acceleration events found</div>;
    }

    return (
        <div className="SiemensContent">
            <div className="SiemensLogoContainer">
                <img
                    className="SiemensLogo"
                    src={siemensLogo}
                    alt=""
                />
            </div>
            <div className="SiemensContentContainer">
                <div className="SiemensContentLeft">
                    <div className="SiemensContentHeader">
                        <h1>Acceleration Event</h1>
                    </div>

                    <div className="SiemensContentInfoContainer">
                        <div className="SiemensContentInfo SiemensContentInfoMargin">
                            <span className="label">Date:</span> {formatTimestamp(currentEntry?.timestamp)}
                        </div>
                        <div className="SiemensContentInfo">
                            <span className="label">Lateral Severity:</span> {currentEntry?.lateral_actual}
                        </div>
                        <div className="SiemensContentInfo SiemensContentInfoMargin">
                            <span className="label">Vertical Severity:</span> {currentEntry?.vertical_actual}
                        </div>
                        <div className="SiemensContentInfo">
                            <span className="label">Lateral Threshold:</span> {currentEntry?.lateral_threshold}
                        </div>
                        <div className="SiemensContentInfo SiemensContentInfoMargin">
                            <span className="label">Vertical Threshold:</span> {currentEntry?.vertical_threshold}
                        </div>
                        <div className="SiemensContentInfo">
                            <span className="label">Lateral Standard Deviation:</span> {currentEntry?.lateral_stddev}
                        </div>
                        <div className="SiemensContentInfo SiemensContentInfoMargin">
                            <span className="label">Vertical Standard Deviation:</span> {currentEntry?.vertical_stddev}
                        </div>
                        <div className="SiemensContentInfo">
                            <span className="label">Train Speed:</span> {currentEntry?.train_speed}mph
                        </div>
                        <div className="SiemensContentInfo">
                            <span className="label">Number of Detections:</span> {currentEntry?.trains}
                        </div>
                    </div>
                    <div className="SiemensContentButton">
                        <Button
                            onClick={goToSession}
                            type="primary"
                            className="Button">
                            View In AIVR Session
                        </Button>
                    </div>
                </div>
                <div className="SiemensContentRight">{eventHistoryGraph}</div>
            </div>
        </div>
    );
};

export default SiemensAssetTab;
